"use client";

import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Table } from "@package/subframe/components/Table";
import {
  FeatherDoorOpen,
  FeatherMoreHorizontal,
  FeatherPencil,
  FeatherPlus,
  FeatherToggleLeft,
  FeatherToggleRight,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import {
  useListRoomsQueryControllerList,
  useUpdateRoomCommandControllerUpdate,
} from "@/generated/rooms/rooms";
import { DEFAULT_PAGE_SIZE } from "@/lib/pagination";

import { RoomFormDialog } from "./RoomFormDialog";

interface RoomsTableProps {
  nurseryId: string;
}

export function RoomsTable({ nurseryId }: RoomsTableProps) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<{
    id: string;
    name: string;
    isActive: boolean;
  } | null>(null);

  const {
    data: roomsData,
    isLoading,
    error,
  } = useListRoomsQueryControllerList(nurseryId, { includeInactive: true });

  const rooms = useMemo(() => roomsData?.data ?? [], [roomsData]);

  const [page, setPage] = useState(1); // 1-based, matches the Pagination component
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // The endpoint returns the full set, so the page is sliced client-side.
  const pagedRooms = useMemo(
    () => rooms.slice((page - 1) * pageSize, page * pageSize),
    [rooms, page, pageSize],
  );

  const { mutate: toggleActive } = useUpdateRoomCommandControllerUpdate({
    mutation: {
      onSuccess: async () => {
        toast.success("Room status updated");
        await queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === "string" &&
            query.queryKey[0].includes("/rooms"),
          refetchType: "all",
        });
      },
      onError: () => {
        toast.error("Failed to update room status");
      },
    },
  });

  const handleCreateClick = () => {
    setEditingRoom(null);
    setDialogOpen(true);
  };

  const handleEditClick = (room: {
    id: string;
    name: string;
    isActive: boolean;
  }) => {
    setEditingRoom(room);
    setDialogOpen(true);
  };

  const handleToggleActive = (room: { id: string; isActive: boolean }) => {
    toggleActive({
      nurseryId,
      roomId: room.id,
      data: { isActive: !room.isActive },
    });
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingRoom(null);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Rooms
          </span>
          <span className="text-body font-body text-subtext-color">
            Manage rooms for this nursery
          </span>
        </div>
        <Button
          variant="brand-primary"
          icon={<FeatherPlus />}
          onClick={handleCreateClick}
        >
          Add Room
        </Button>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Spinner className="h-8 w-8 text-primary" />
        </div>
      ) : error ? (
        <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
          <IconWithLogoBackground variant="error" size="x-large" />
          <span className="text-body font-body text-subtext-color">
            Error loading rooms. Please try again.
          </span>
        </div>
      ) : rooms.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center py-12 gap-4 rounded-md border border-solid border-secondary-beige-200">
          <IconWithLogoBackground
            variant="neutral"
            size="x-large"
            icon={<FeatherDoorOpen />}
          />
          <div className="flex flex-col items-center gap-2">
            <span className="text-heading-3 font-heading-3 text-default-font">
              No rooms yet
            </span>
            <span className="text-body font-body text-subtext-color text-center max-w-md">
              Add rooms to organise logs within this nursery.
            </span>
          </div>
          <Button
            variant="brand-primary"
            icon={<FeatherPlus />}
            onClick={handleCreateClick}
          >
            Add Room
          </Button>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell className="items-center justify-end">
                  Actions
                </Table.HeaderCell>
              </Table.HeaderRow>
            }
          >
            {pagedRooms.map((room) => (
              <Table.Row key={room.id}>
                <Table.Cell>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {room.name}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {room.isActive ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="neutral">Inactive</Badge>
                  )}
                </Table.Cell>
                <Table.Cell variant="aligned-right">
                  <div className="flex items-center justify-end gap-2">
                    <SubframeCore.DropdownMenu.Root>
                      <SubframeCore.DropdownMenu.Trigger asChild={true}>
                        <IconButton
                          variant="neutral-primary"
                          size="medium"
                          icon={<FeatherMoreHorizontal />}
                        />
                      </SubframeCore.DropdownMenu.Trigger>
                      <SubframeCore.DropdownMenu.Portal>
                        <SubframeCore.DropdownMenu.Content
                          side="bottom"
                          align="end"
                          sideOffset={4}
                          asChild={true}
                        >
                          <DropdownMenu>
                            <DropdownMenu.DropdownItem
                              icon={<FeatherPencil />}
                              onClick={() =>
                                handleEditClick({
                                  id: room.id,
                                  name: room.name,
                                  isActive: room.isActive,
                                })
                              }
                            >
                              Edit
                            </DropdownMenu.DropdownItem>
                            <DropdownMenu.DropdownItem
                              icon={
                                room.isActive ? (
                                  <FeatherToggleLeft />
                                ) : (
                                  <FeatherToggleRight />
                                )
                              }
                              onClick={() =>
                                handleToggleActive({
                                  id: room.id,
                                  isActive: room.isActive,
                                })
                              }
                            >
                              {room.isActive ? "Deactivate" : "Activate"}
                            </DropdownMenu.DropdownItem>
                          </DropdownMenu>
                        </SubframeCore.DropdownMenu.Content>
                      </SubframeCore.DropdownMenu.Portal>
                    </SubframeCore.DropdownMenu.Root>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table>

          <Pagination
            className="pt-4"
            page={page}
            pageSize={pageSize}
            totalCount={rooms.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            itemNoun="rooms"
          />
        </div>
      )}

      <RoomFormDialog
        nurseryId={nurseryId}
        room={editingRoom}
        open={dialogOpen}
        onOpenChange={handleDialogClose}
      />
    </div>
  );
}
